import { useState } from 'react';
import { color, fontSize, radius, elevation } from '../../theme/tokens';
import { AnalystModal } from './AnalystModal';

/**
 * The **AI** header control (spec 006 T112, FR-A01/FR-A02).
 *
 * ## ⚠ On four pages, and deliberately NOT on Settings or Login
 *
 * It is opted into per page rather than mounted in the shell, so the two pages that must not have
 * it cannot acquire one by inheriting a default. Settings carries directory state and Login has
 * no session — neither has a book to ask about.
 *
 * ## ⚠ Text on the brand gradient is `color.onBrand`, never `color.onAccent`
 *
 * The gradient does not follow the theme, so it needs the one colour that also does not. Using
 * `onAccent` here puts near-black text on teal in dark mode — the same mistake that put it on the
 * sign-in button. A literal `'#fff'` would work and is a lint error: `theme/global.css` owns every
 * colour value.
 */

/**
 * ⚠ **Takes no props, deliberately.** The analyst is not page-scoped: the same question asks the
 * same thing from every page, so nothing about where the button sits may reach the turn. It used
 * to forward a `pageHint`, which the Resolver was told to use only when the question named no
 * scope — and the assumptions line then claimed the page's region even on turns where the spec
 * had no region filter at all, reporting a narrowing that never happened.
 */
export function AnalystButton() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        // The control is the word plus a glyph; the accessible name says what it does, because
        // "AI" alone names a technology rather than an action.
        aria-label="Ask the analyst about this book of business"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          // Composed from the two brand tokens, exactly as the sidebar mark and the sign-in
          // button are — there is no single `brandGradient` token, and inventing one here would
          // put a third spelling of the same gradient in the codebase.
          background: `linear-gradient(150deg, ${color.brandFrom}, ${color.brandTo})`,
          // ⚠ onBrand, not onAccent — see the note above.
          color: color.onBrand,
          border: 'none',
          borderRadius: radius.lg,
          padding: '6px 12px',
          fontSize: fontSize.caption,
          fontWeight: 600,
          cursor: 'pointer',
          boxShadow: elevation.brand,
        }}
      >
        <span aria-hidden="true">✦</span>
        AI
      </button>

      <AnalystModal open={open} onClose={() => setOpen(false)} />
    </>
  );
}
